import { useState } from 'react'
import { Circle } from '@/components/icons'
import { AlertBadge } from '@/components/ui/alert-badge'
import type { QuickStartStep } from './QuickStartGuideDrawer'

interface QuickStartStepListProps {
  steps: QuickStartStep[]
  activeStepId: string | undefined
  onStepClick: (stepId: string) => void
  onStepOpen?: (stepId: string) => void
}

export function QuickStartStepList({
  steps,
  activeStepId,
  onStepClick,
  onStepOpen,
}: QuickStartStepListProps) {
  return (
    <div className="bg-background-brand-subtle rounded-2xl p-4 flex flex-col gap-2 w-full">
      {steps.map((step, index) => {
        const isActive = step.id === activeStepId
        const previousIncomplete = index > 0 && !steps[index - 1].completed
        const isLocked = !step.completed && previousIncomplete
        const isTappable = isActive && !step.completed && !isLocked
        return (
          <button
            key={step.id}
            type="button"
            onClick={() => {
              if (isTappable) {
                onStepClick(step.id)
                onStepOpen?.(step.id)
              }
            }}
            disabled={!isTappable}
            className={`bg-surface-L0 rounded-lg px-4 py-3 flex items-center gap-4 text-left transition-colors ${
              isActive ? 'border border-positive' : ''
            } ${isLocked ? 'opacity-50' : ''} ${isTappable ? 'cursor-pointer hover:bg-surface-L2/50' : ''}`}
          >
            {step.completed ? (
              <AlertBadge variant="valid" className="size-5" />
            ) : (
              <div className="bg-surface-L2 rounded-full p-0.5">
                <Circle className="size-5 text-content-primary-subtle" />
              </div>
            )}
            <p className={`text-xs font-medium ${isActive ? 'text-content-primary-bold' : 'text-content-primary-subtle'}`}>
              {step.label}
            </p>
          </button>
        )
      })}
    </div>
  )
}

export function useStepSelection(steps: QuickStartStep[], firstIncompleteId: string | undefined) {
  const [selectedStepId, setSelectedStepId] = useState<string | undefined>(firstIncompleteId)

  const effectiveSelectedId = selectedStepId && !steps.find((s) => s.id === selectedStepId)?.completed
    ? selectedStepId
    : firstIncompleteId

  const selectedStepLabel = steps.find((s) => s.id === effectiveSelectedId)?.label ?? 'Continue'

  return { effectiveSelectedId, selectedStepLabel, setSelectedStepId }
}
